import { Client } from "@/Common/Api/Client";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PharmacyOrdersFilterSettingsModel } from "../Models/PharmacyOrdersFilterSettingsModel";
import { IPharmacyOrdersFilterSettings } from "./Dto/IPharmacyOrdersFilterSettings";
import { IGetPharmacyOrdersRequest } from "./Dto/Request/IGetPharmacyOrdersRequest";
import { IGetPharmacyOrderByOrderIdResponse } from "./Dto/Response/IGetPharmacyOrderByOrderIdResponse";
import { IGetPharmacyOrderFormUrl } from "./Dto/Response/IGetPharmacyOrderFormUrl";
import { IGetPharmacyOrderPdfFileName } from "./Dto/Response/IGetPharmacyOrderPdfFileName";
import { IGetPharmacyOrdersResponse } from "./Dto/Response/IGetPharmacyOrdersResponse";

class PharmacyOrdersClient {
    /**
     * Get a list of pharmacy orders based on the request.
     * @param request A request to fetch pharmacy orders.
     * @returns A list of pharmacy orders based on the request.
     */
    public static async fetchPharmacyOrders(request: IGetPharmacyOrdersRequest): Promise<IGetPharmacyOrdersResponse | null> {
        const response = await Client.postAsync<IGetPharmacyOrdersResponse>(
            ServerOperations.Server.Myle_PharmacyOrder_GetPharmacyOrders,
            request
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }

    /**
     * Complete pharmacy orders by Ids.
     * @param orderIds The pharmacy orders Ids to complete.
     * @param sendNotification If a notification should be send to the patient.
     * @returns A boolean value if the orders were completed successfully.
     */
    public static async completePharmacyOrdersAsync(orderIds: string[], sendNotification: boolean): Promise<boolean> {
        const response = await Client.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_PharmacyOrder_CompletePharmacyOrders,
            {
                OrderIds: orderIds,
                SendNotification: sendNotification
            }
        );

        return response.isSuccess;
    }

    /**
     * Archive/Restore pharmacy orders by Ids.
     * @param orderIds The pharmacy orders Ids to archive/restore.
     * @param shouldArchive A boolean whether to archive/restore the orders.
     * @returns A boolean value if the orders were archived/restore successfully.
     */
    public static async updatePharmacyOrdersArchiveRestore(orderIds: string[], shouldArchive: boolean): Promise<boolean> {
        const response = await Client.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_PharmacyOrder_ArchiveRestoreOrders,
            {
                PharmacyOrderIds: orderIds,
                ShouldArchive: shouldArchive
            }
        );

        return response.isSuccess;
    }

    /**
     * Save the filter setting of the table for Pharmacy orders.
     * @param orderSettings the filter settings to save in the user setting.
     */
    public static async savePharamcyOrderFilterSettings(orderSettings: PharmacyOrdersFilterSettingsModel): Promise<void> {
        const settings = PharmacyOrdersFilterSettingsModel.toResponseModel(orderSettings);

        cacheStore.UserSettings.PharmacyOrdersFilterSettings = settings;

        const requestData = {
            UserSettings: {
                PharmacyOrdersFilterSettings: settings
            },
            UserId: sessionStore.userId
        };

        await Client.postAsync<IPharmacyOrdersFilterSettings>(ServerOperations.Server.Myle_UpdateUserSettings, requestData);
    }

    /**
     * Retrieves the details of a pharmacy order by its unique order ID.
     * @param {string} orderId - The unique identifier of the pharmacy order to be retrieved.
     * */
    public static async getPharmacyOrderByOrderId(orderId: string): Promise<IGetPharmacyOrderByOrderIdResponse> {
        const response = await Client.postAsync<IGetPharmacyOrderByOrderIdResponse>(
            ServerOperations.Server.Myle_PharmacyOrder_GetPharmacyOrderByOrderId,
            {
                OrderId: orderId
            }
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }

    /**
     *Generates the file name for the PDF version of a pharmacy order to generate the pdf preview by its unique order ID.
     * @param {string} orderId - The unique identifier of the pharmacy order to be retrieved.
     * */
    public static async generatePharmacyOrderPdfFileName(orderId: string): Promise<IGetPharmacyOrderPdfFileName> {
        const currentEmployee = sessionStore.getCurrentEmployee();
        const response = await Client.postAsync<IGetPharmacyOrderPdfFileName>(
            ServerOperations.Server.Myle_PharmacyOrder_GeneratePharmacyOrderPdf,
            {
                OrderId: orderId,
                PharmacyName: currentEmployee.getActiveSite().Model.ClinicalName,
                IsActiveSiteQuebec: currentEmployee.isActiveSiteQuebec(),
                IsActiveSiteBc: currentEmployee.isActiveSiteBC()
            }
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }

    /**
     * Sends an SMS message by order ID to remind the patient to fill out the form for the pharmacy.
     * @param orderId The ID of the pharmacy order to send the manual reminder.
     * @returns A boolean value indicating whether the reminder was sent successfully.
     */
    public static async sendSmsReminderToPatient(orderId: string): Promise<boolean> {
        const response = await Client.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_PharmacyOrder_SendPatientReminder,
            {
                OrderId: orderId
            }
        );

        return response.isSuccess;
    }

    /**
     * File a pharmacy order.
     * @param orderId The Id of the pharmacy order to be filed.
     * @returns A boolean value indicating whether the pharmacy order is successfully filed.
     */
    public static async filePharmacyOrder(orderId: string): Promise<boolean> {
        const response = await Client.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_PharmacyOrder_FilePharmacyOrder,
            {
                OrderId: orderId
            }
        );

        return response.isSuccess;
    }

    /**
     * Retrieves the URL to open the order patient form for a specific order.
     * @param orderId The unique identifier of the order to be opened in the patient form.
     * @returns A promise that resolves to an object containing the URL for the order form.
     */
    public static async getOrderFormUrlAsync(orderId: string): Promise<IGetPharmacyOrderFormUrl | null> {
        const response = await Client.postAsync<IGetPharmacyOrderFormUrl>(
            ServerOperations.Server.Myle_PharmacyOrder_GeneratePharmacyOrderFormUrl,
            {
                OrderId: orderId
            }
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }
}

export { PharmacyOrdersClient };
